import type { LawFirm } from '@common/types/lawfirms';
import {
    type AuditEvent,
    type AuditEventAction,
    SUPPORT_ACCESS_DURATION_HOURS,
    type SupportAccessDurationHours,
    type SupportAccessGrant,
    type SupportAccessStatus,
} from '@common/types/supportAccess';
import {
    Box,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { DateTime } from 'luxon';
import { useState } from 'react';
import LoadingButton from '@/components/Buttons/LoadingButton';
import DataTable, { type Column } from '@/components/DataTable';
import { useAuditEvents, useSupportAccessStatus } from '@/queries/supportAccess';
import { giveSupportAccess, revokeSupportAccess } from '@/services/SupportAccessService';
import { errorSnackbar, successSnackbar } from '@/utils/snackbar';

interface Props {
    lawFirm: LawFirm;
    /** One of the firm's own admins. Others (Lexo Admins) see the page but can't change access */
    canManage: boolean;
}

const DURATION_LABELS: Record<SupportAccessDurationHours, string> = {
    1: '1 hour',
    24: '24 hours',
    168: '7 days',
};

const ACTION_LABELS: Record<AuditEventAction, string> = {
    'grant.access': 'Lexo opened client data',
    'grant.created': 'Access given',
    'grant.revoked': 'Access revoked',
    'firm.private_enabled': 'Firm made private',
    'firm.private_disabled': 'Firm made not private',
    'billing.price_changed': 'Billed price corrected',
};

// Within the API's limit of 200 per page, so there's no "All"
const AUDIT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function formatDateTime(iso: string) {
    return DateTime.fromISO(iso).toLocaleString(DateTime.DATETIME_MED);
}

function describeGrantState(grant: SupportAccessGrant, activeGrantId: string | undefined) {
    if (grant.supportAccessGrantId === activeGrantId) return 'Active';
    if (grant.revokedAt) return `Revoked ${formatDateTime(grant.revokedAt)}`;
    return 'Expired';
}

function describeAuditEvent(event: AuditEvent) {
    if (event.resourceId) return `${event.resourceType ?? 'Record'} ${event.resourceId}`;
    if (event.action === 'grant.access' && event.requestPath) {
        return `${event.requestMethod ?? ''} ${event.requestPath}`.trim();
    }
    return '';
}

const AUDIT_COLUMNS: Column<AuditEvent>[] = [
    {
        id: 'occurredAt',
        label: 'When',
        _render: (event) => formatDateTime(event.occurredAt),
    },
    { id: 'actorEmail', label: 'Who', _render: (event) => event.actorEmail ?? '' },
    { id: 'action', label: 'What', _render: (event) => ACTION_LABELS[event.action] },
    { id: 'detail', label: 'Detail', _render: describeAuditEvent },
];

export default function SupportAccess({ lawFirm, canManage }: Props) {
    const lawFirmId = lawFirm.lawFirmId;
    const queryClient = useQueryClient();
    const [durationHours, setDurationHours] = useState<SupportAccessDurationHours>(24);
    const [reason, setReason] = useState('');
    const [auditPage, setAuditPage] = useState(0);
    const [auditPageSize, setAuditPageSize] = useState(25);

    const { data: status } = useSupportAccessStatus(lawFirmId);
    const { data: auditEvents, isLoading: isAuditLoading } = useAuditEvents(
        lawFirmId,
        auditPage,
        auditPageSize,
    );
    const activeGrant = status?.activeGrant ?? null;

    const onChanged = (nextStatus: SupportAccessStatus) => {
        queryClient.setQueryData(['supportAccess', lawFirmId], nextStatus);
        void queryClient.invalidateQueries({
            queryKey: ['supportAccess', lawFirmId, 'auditEvents'],
        });
    };
    const onError = (error: unknown) => {
        errorSnackbar(
            isAxiosError(error) && typeof error.response?.data === 'string'
                ? error.response.data
                : 'Something went wrong. Please try again.',
        );
    };

    const give = useMutation({
        mutationFn: () =>
            giveSupportAccess(lawFirmId, { durationHours, reason: reason.trim() || undefined }),
        onSuccess: (nextStatus) => {
            onChanged(nextStatus);
            setReason('');
            successSnackbar(`Lexo has access for ${DURATION_LABELS[durationHours]}`);
        },
        onError,
    });
    const revoke = useMutation({
        mutationFn: (supportAccessGrantId: string) =>
            revokeSupportAccess(lawFirmId, supportAccessGrantId),
        onSuccess: (nextStatus) => {
            onChanged(nextStatus);
            successSnackbar("Lexo's access has ended");
        },
        onError,
    });

    return (
        <Stack spacing={3} sx={{ maxWidth: '900px' }}>
            <Stack spacing={1}>
                <Typography variant="h1">Lexo Access</Typography>
                <Typography variant="body2" sx={{ color: 'neutral.N_500' }}>
                    {lawFirm.companyName} is private. Lexo staff can't see your matters, contacts or
                    ID verifications unless one of your admins gives them access. Access ends on its
                    own, and you can end it sooner. Everything Lexo opens while it has access is
                    recorded below.
                </Typography>
            </Stack>

            <Box
                sx={{
                    border: '1px solid',
                    borderColor: activeGrant ? 'success.main' : 'neutral.N_100',
                    bgcolor: activeGrant ? 'success.S_50' : 'neutral.N_50',
                    borderRadius: 2,
                    padding: 2,
                }}
            >
                {activeGrant ? (
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        justifyContent="space-between"
                        alignItems={{ xs: 'flex-start', sm: 'center' }}
                    >
                        <Stack spacing={0.5}>
                            <Typography variant="h3">
                                Lexo has access until {formatDateTime(activeGrant.expiresAt)}
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                Given by {activeGrant.grantedByName ?? 'a firm admin'} on{' '}
                                {formatDateTime(activeGrant.startsAt)}
                                {activeGrant.reason ? `: ${activeGrant.reason}` : ''}
                            </Typography>
                        </Stack>
                        {canManage && (
                            <LoadingButton
                                variant="outlined"
                                color="error"
                                loading={revoke.isPending}
                                onClick={() => revoke.mutate(activeGrant.supportAccessGrantId)}
                            >
                                End access now
                            </LoadingButton>
                        )}
                    </Stack>
                ) : (
                    <Stack spacing={2}>
                        <Stack spacing={0.5}>
                            <Typography variant="h3">Lexo doesn't have access</Typography>
                            <Typography variant="body2" sx={{ color: 'neutral.N_700' }}>
                                {canManage
                                    ? 'If you need help from Lexo with a matter, give them access for as long as they need.'
                                    : "Only the firm's admins can give Lexo access."}
                            </Typography>
                        </Stack>
                        {canManage && (
                            <Stack
                                direction={{ xs: 'column', sm: 'row' }}
                                spacing={2}
                                alignItems={{ xs: 'stretch', sm: 'center' }}
                            >
                                <FormControl size="small" sx={{ minWidth: '160px' }}>
                                    <InputLabel id="support-access-duration">For</InputLabel>
                                    <Select
                                        labelId="support-access-duration"
                                        label="For"
                                        value={durationHours}
                                        onChange={(event) =>
                                            setDurationHours(
                                                event.target.value as SupportAccessDurationHours,
                                            )
                                        }
                                    >
                                        {SUPPORT_ACCESS_DURATION_HOURS.map((hours) => (
                                            <MenuItem key={hours} value={hours}>
                                                {DURATION_LABELS[hours]}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <TextField
                                    size="small"
                                    label="Reason (optional)"
                                    value={reason}
                                    onChange={(event) => setReason(event.target.value)}
                                    inputProps={{ maxLength: 500 }}
                                    sx={{ flex: 1 }}
                                />
                                <LoadingButton
                                    variant="contained"
                                    loading={give.isPending}
                                    onClick={() => give.mutate()}
                                >
                                    Give Lexo access
                                </LoadingButton>
                            </Stack>
                        )}
                    </Stack>
                )}
            </Box>

            {!!status?.recentGrants.length && (
                <Stack spacing={1}>
                    <Typography variant="h3">Recent access</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Given</TableCell>
                                <TableCell>Until</TableCell>
                                <TableCell>Given by</TableCell>
                                <TableCell>Reason</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {status.recentGrants.map((grant) => (
                                <TableRow key={grant.supportAccessGrantId}>
                                    <TableCell>{formatDateTime(grant.startsAt)}</TableCell>
                                    <TableCell>{formatDateTime(grant.expiresAt)}</TableCell>
                                    <TableCell>{grant.grantedByName ?? ''}</TableCell>
                                    <TableCell>{grant.reason ?? ''}</TableCell>
                                    <TableCell>
                                        {describeGrantState(
                                            grant,
                                            activeGrant?.supportAccessGrantId,
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Stack>
            )}

            <Stack spacing={1}>
                <Typography variant="h3">Activity</Typography>
                <DataTable
                    generateKeyFrom="auditEventId"
                    isLoading={isAuditLoading}
                    columns={AUDIT_COLUMNS}
                    rows={auditEvents?.auditEvents ?? []}
                    count={auditEvents?.total ?? 0}
                    page={auditPage}
                    rowsPerPage={auditPageSize}
                    rowsPerPageOptions={AUDIT_PAGE_SIZE_OPTIONS}
                    handleChangePage={(_event, newPage) => setAuditPage(newPage)}
                    handleChangeRowsPerPage={(event) => {
                        setAuditPageSize(Number(event.target.value));
                        setAuditPage(0);
                    }}
                />
            </Stack>
        </Stack>
    );
}
